import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { api, ApiError, type Gift, type SpaceDetail } from '../../lib/api'
import { layout } from '../../design/tokens'
import { TabBar } from '../../components/TabBar'

const ETATS = [
  { code: 'idea', libelle: 'Idée' },
  { code: 'bought', libelle: 'Acheté' },
  { code: 'given', libelle: 'Offert' },
] as const

/**
 * Cadeaux — planche 14.
 *
 * Le masquage n'est pas fait ici : une fiche destinée à quelqu'un ne parvient jamais à son
 * appareil, le serveur l'exclut de la requête (règle R2). Cet écran n'a donc rien à cacher —
 * ce qu'il reçoit, il peut le montrer.
 */
export function GiftsScreen() {
  const { spaceId = '' } = useParams()
  const navigate = useNavigate()
  const [ajout, setAjout] = useState(false)

  const { data: espace } = useQuery<SpaceDetail>({
    queryKey: ['espace', spaceId],
    queryFn: () => api.space(spaceId),
  })

  const { data, isPending } = useQuery<readonly Gift[]>({
    queryKey: ['cadeaux', spaceId],
    queryFn: () => api.gifts(spaceId),
  })

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter, paddingBottom: 32 }}
    >
      <header className="flex items-center gap-3" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="flex shrink-0 items-center justify-center rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ width: 40, height: 40 }}
        >
          ←
        </button>
        <h1 className="grow truncate font-serif text-screen">Cadeaux</h1>

      </header>

      {/* Même règle que l'agenda : le « + » de la barre ajoute ce que l'écran contient. */}
      <TabBar onAdd={() => setAjout(true)} activeSpaceId={spaceId} />

      {ajout ? (
        <Formulaire
          spaceId={spaceId}
          membres={espace?.members ?? []}
          onDone={() => setAjout(false)}
        />
      ) : null}

      {isPending ? (
        <p className="text-body text-ink-muted">Chargement…</p>
      ) : data && data.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {data.map((cadeau) => (
            <Ligne key={cadeau.id} spaceId={spaceId} cadeau={cadeau} />
          ))}
        </ul>
      ) : (
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">Aucune idée pour l'instant.</p>
          <p className="mt-2 text-body text-ink-muted">
            Une idée destinée à un membre de l'espace lui est cachée : il ne la reçoit pas, même
            en cherchant.
          </p>
        </div>
      )}
    </main>
  )
}

function Ligne({ spaceId, cadeau }: { spaceId: string; cadeau: Gift }) {
  const queryClient = useQueryClient()
  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['cadeaux', spaceId] })

  const corriger = useMutation({
    mutationFn: (patch: Parameters<typeof api.patchGift>[2]) =>
      api.patchGift(spaceId, cadeau.id, patch),
    onSuccess: rafraichir,
  })

  const supprimer = useMutation({
    mutationFn: () => api.deleteGift(spaceId, cadeau.id),
    onSuccess: rafraichir,
  })

  return (
    <li className="rounded-card border border-border bg-card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="grow text-card-title font-semibold">{cadeau.title}</p>
        {cadeau.estimatedPriceCents !== null ? (
          <span className="shrink-0 text-meta text-ink-muted">
            {(cadeau.estimatedPriceCents / 100).toLocaleString('fr-FR', {
              style: 'currency',
              currency: 'EUR',
            })}
          </span>
        ) : null}
      </div>

      <p className="mt-1 text-meta text-ink-muted">
        {cadeau.recipientName ? `Pour ${cadeau.recipientName}` : 'Sans destinataire'}
        {cadeau.occasion ? ` · ${cadeau.occasion}` : ''}
        {cadeau.itemTitle ? ` · depuis « ${cadeau.itemTitle} »` : ''}
      </p>

      <ul className="mt-3 flex gap-2">
        {ETATS.map((etat) => (
          <li key={etat.code}>
            <button
              type="button"
              onClick={() => corriger.mutate({ status: etat.code })}
              aria-pressed={cadeau.status === etat.code}
              className="rounded-full px-3 text-meta font-semibold"
              style={{
                minHeight: 36,
                background:
                  cadeau.status === etat.code ? 'var(--color-accent)' : 'var(--color-surface)',
                color: cadeau.status === etat.code ? 'var(--color-card)' : 'var(--color-ink)',
                border: `1px solid ${cadeau.status === etat.code ? 'var(--color-accent)' : 'var(--color-border)'}`,
              }}
            >
              {etat.libelle}
            </button>
          </li>
        ))}
      </ul>

      {cadeau.recipientUserId ? (
        <label className="mt-3 flex items-center gap-3">
          <input
            type="checkbox"
            checked={cadeau.hiddenFromRecipient}
            onChange={(evenement) =>
              corriger.mutate({ hiddenFromRecipient: evenement.target.checked })
            }
            style={{ width: 20, height: 20 }}
          />
          <span className="text-meta">
            {/* Le mot compte : « caché » dit ce qui se passe vraiment, le serveur ne lui envoie
                rien. « Discret » ou « privé » laisserait croire à un simple masquage d'affichage. */}
            Caché à {cadeau.recipientName ?? 'son destinataire'}
          </span>
        </label>
      ) : null}

      <button
        type="button"
        onClick={() => supprimer.mutate()}
        className="mt-3 text-meta font-semibold text-ink-muted"
      >
        Retirer
      </button>
    </li>
  )
}

function Formulaire({
  spaceId,
  membres,
  onDone,
}: {
  spaceId: string
  membres: readonly { readonly userId: string; readonly displayName: string }[]
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const [titre, setTitre] = useState('')
  const [destinataire, setDestinataire] = useState('')
  const [prix, setPrix] = useState('')
  const [occasion, setOccasion] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  const creer = useMutation({
    mutationFn: () =>
      api.createGift(spaceId, {
        title: titre.trim(),
        ...(destinataire ? { recipientUserId: destinataire } : {}),
        // Saisi en euros, transmis en centimes entiers : un prix en flottant finit par se voir
        // à l'addition (écart E4).
        ...(prix.trim()
          ? { estimatedPriceCents: Math.round(Number(prix.replace(',', '.')) * 100) }
          : {}),
        ...(occasion.trim() ? { occasion: occasion.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cadeaux', spaceId] })
      onDone()
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'ajout a échoué."),
  })

  return (
    <section className="mb-4 rounded-card border border-border bg-card p-4">
      <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="titre-cadeau">
        Idée
      </label>
      <input
        id="titre-cadeau"
        autoFocus
        value={titre}
        onChange={(evenement) => setTitre(evenement.target.value)}
        placeholder="Casque audio"
        className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      />

      <label
        className="mt-4 block text-label font-semibold uppercase text-ink-muted"
        htmlFor="destinataire-cadeau"
      >
        Pour qui
      </label>
      <select
        id="destinataire-cadeau"
        value={destinataire}
        onChange={(evenement) => setDestinataire(evenement.target.value)}
        className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      >
        <option value="">Quelqu'un hors de l'espace</option>
        {membres.map((membre) => (
          <option key={membre.userId} value={membre.userId}>
            {membre.displayName}
          </option>
        ))}
      </select>
      <p className="mt-2 text-meta text-ink-muted">
        Choisir un membre le cache automatiquement : il ne verra rien, ni dans la liste, ni dans
        la recherche, ni dans les nouveautés.
      </p>

      <div className="mt-4 flex gap-3">
        <div className="grow">
          <label
            className="block text-label font-semibold uppercase text-ink-muted"
            htmlFor="prix-cadeau"
          >
            Prix (facultatif)
          </label>
          <input
            id="prix-cadeau"
            inputMode="decimal"
            value={prix}
            onChange={(evenement) => setPrix(evenement.target.value)}
            placeholder="49,90"
            className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 56, fontSize: 17 }}
          />
        </div>
        <div className="grow">
          <label
            className="block text-label font-semibold uppercase text-ink-muted"
            htmlFor="occasion-cadeau"
          >
            Occasion
          </label>
          <input
            id="occasion-cadeau"
            value={occasion}
            onChange={(evenement) => setOccasion(evenement.target.value)}
            placeholder="Noël"
            className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 56, fontSize: 17 }}
          />
        </div>
      </div>

      {erreur ? <p className="mt-3 text-meta text-danger">{erreur}</p> : null}

      <button
        type="button"
        disabled={titre.trim().length === 0 || creer.isPending}
        onClick={() => creer.mutate()}
        className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: layout.primaryButtonHeight }}
      >
        {creer.isPending ? 'Ajout…' : 'Ajouter'}
      </button>
    </section>
  )
}
